import { useQuery } from '@tanstack/react-query'

import { billingApi, invitationsApi, membersApi, projectsApi, tasksApi } from './api'
import { queryKeys } from './queryClient'
import type { Project, UUID } from './types'

/**
 * Shared read hooks.
 *
 * They exist so that route components state *what* they need rather than how it
 * is fetched, and so every workspace-scoped key carries the slug — the one
 * mistake that would let one workspace's roster render under another's URL.
 */

/**
 * Projects visible to the caller, narrowed to one workspace.
 *
 * `GET /api/projects` is the frozen v1 endpoint: it is scoped server-side to
 * the caller's memberships, which may span more than one workspace depending on
 * LEGACY_CROSS_ORG_VISIBILITY. Rows carry `organization_id`, so the filter here
 * is exact when the column is populated and simply passes older, un-backfilled
 * rows through rather than hiding a user's own data from them.
 */
export function useProjects(slug: string, organizationId: UUID | null) {
  return useQuery({
    queryKey: queryKeys.projectList(slug),
    queryFn: ({ signal }) => projectsApi.list({ signal }),
    select: (projects: Project[]) =>
      organizationId
        ? projects.filter((p) => !p.organization_id || p.organization_id === organizationId)
        : projects,
  })
}

export function useMembers(slug: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.members(slug),
    queryFn: ({ signal }) => membersApi.list(slug, { signal }),
    enabled,
  })
}

export function useInvitations(slug: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.invitations(slug),
    queryFn: ({ signal }) => invitationsApi.list(slug, { signal }),
    enabled,
  })
}

export function useInvitationInbox(enabled = true) {
  return useQuery({
    queryKey: queryKeys.invitationInbox,
    queryFn: ({ signal }) => invitationsApi.inbox({ signal }),
    enabled,
  })
}

export function useBillingSummary(slug: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.billingSummary(slug),
    queryFn: ({ signal }) => billingApi.summary(slug, { signal }),
    enabled,
  })
}

/** Issue search for the top bar. Disabled below two characters. */
export function useIssueSearch(slug: string, term: string) {
  const trimmed = term.trim()
  return useQuery({
    queryKey: [...queryKeys.search(trimmed), slug],
    queryFn: ({ signal }) => tasksApi.list({ search: trimmed, per_page: 6 }, { signal }),
    enabled: trimmed.length >= 2,
    staleTime: 15_000,
  })
}
